import { useEffect, useCallback, useState } from 'react';
import type { DashboardSummary, Dashboard } from '../types/dashboard';
import { createDashboard, deleteDashboard, saveConfig } from '../utils/api';
import { ConfirmationDeleteModal } from './ConfirmationDeleteModal';
import { ConfirmHomePageModal } from './ConfirmHomePageModal';
import { ImportModal } from './ImportModal';
import { generateUUID } from '@/utils/uuid';
import './DashboardList.css';

function parseBundleKey(sourceKey: string): { entityType: string; bundle: string } | null {
  if (!sourceKey) return null;
  const parts = sourceKey.split(':');
  if (parts.length < 2) return null;
  return { entityType: parts[0], bundle: parts[1] };
}

function groupDashboards(
  dashboards: DashboardSummary[],
): Array<{ label: string; items: DashboardSummary[] }> {
  const groups = new Map<string, DashboardSummary[]>();
  for (const d of dashboards) {
    const label = d.bundleLabel === 'standalone:dashboard'
      ? 'Свободные дашборды'
      : d.bundleLabel || 'Без категории';
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label)!.push(d);
  }
  return Array.from(groups.entries()).map(([label, items]) => ({ label, items }));
}

function sortGroups(
  groups: Array<{ label: string; items: DashboardSummary[] }>,
): Array<{ label: string; items: DashboardSummary[] }> {
  return [...groups].sort((a, b) => a.label.localeCompare(b.label, 'ru'));
}

function sortItems(items: DashboardSummary[]): DashboardSummary[] {
  return [...items].sort((a, b) => {
    if (!a.updatedAt && !b.updatedAt) return a.title.localeCompare(b.title);
    if (!a.updatedAt) return 1;
    if (!b.updatedAt) return -1;
    const dateDiff = new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    if (dateDiff !== 0) return dateDiff;
    return a.title.localeCompare(b.title);
  });
}

function getGroupIcon(entityType: string): string {
  switch (entityType) {
    case 'node': return 'las la-cog';
    case 'taxonomy_term': return 'las la-tags';
    default: return 'las la-file-alt';
  }
}

function getItemIcon(sourceKey: string): string {
  const entityType = parseBundleKey(sourceKey)?.entityType;
  switch (entityType) {
    case 'node': return 'la-chart-pie';
    case 'taxonomy_term': return 'la-tag';
    default: return 'la-chart-bar';
  }
}

function getTypeLabel(type: string): string {
  return type === 'standalone' ? 'Свободный' : 'Привязанный';
}

function getTypeBadgeClass(type: string): string {
  return type === 'standalone'
    ? 'badge text-bg-success'
    : 'badge text-bg-secondary';
}

const ENTITY_TYPES = [
  { sourceKey: 'node:process', label: 'Процессы' },
  { sourceKey: 'taxonomy_term:areas', label: 'Области' },
  { sourceKey: 'taxonomy_term:systems', label: 'Системы' },
  { sourceKey: 'taxonomy_term:projects', label: 'Проекты' },
];

interface DashboardListProps {
  onClose: () => void;
}

export function DashboardList({ onClose }: DashboardListProps) {
  const [dashboards, setDashboards] = useState<DashboardSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createTitle, setCreateTitle] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createType, setCreateType] = useState<'standalone' | 'bundle'>('standalone');
  const [createEntityType, setCreateEntityType] = useState('');

  const [dashboardToDelete, setDashboardToDelete] = useState<DashboardSummary | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [homePageCandidate, setHomePageCandidate] = useState<DashboardSummary | null>(null);

  const toggleGroup = useCallback((label: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  }, []);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  const handleSetHomePage = useCallback((dashboard: DashboardSummary) => {
    if (dashboard.isHomePage) return;
    setHomePageCandidate(dashboard);
  }, []);

  const handleHomePageConfirmed = useCallback(() => {
    const candidate = homePageCandidate;
    setHomePageCandidate(null);
    if (!candidate) return;
    setDashboards(prev => prev.map(d => ({
      ...d,
      isHomePage: d.uuid === candidate.uuid,
    })));
    setToastMessage('Дашборд назначен главной страницей');
    setTimeout(() => setToastMessage(null), 3000);
  }, [homePageCandidate]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    const controller = new AbortController();

    setLoading(true);
    setError(null);

    globalThis
      .fetch('/api/dashboards', { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const data: DashboardSummary[] = await response.json();
        setDashboards(data);
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          setError(err.message || 'Ошибка загрузки');
        }
      })
      .finally(() => {
        setLoading(false);
      });

    return () => controller.abort();
  }, []);

  useEffect(() => {
    const handler = () => {
      globalThis.fetch('/api/dashboards')
        .then(r => r.ok ? r.json() : Promise.reject())
        .then(data => setDashboards(data))
        .catch(() => {});
    };
    window.addEventListener('dashboard:refresh-list', handler);
    return () => window.removeEventListener('dashboard:refresh-list', handler);
  }, []);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleOpenCreateModal = () => {
    setShowCreateModal(true);
    setCreateTitle('');
    setCreateError(null);
    setCreateType('standalone');
    setCreateEntityType('');
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setCreateTitle('');
    setCreateError(null);
    setCreateType('standalone');
    setCreateEntityType('');
  };

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (createType === 'bundle' && !createEntityType) return;
    setCreateLoading(true);
    setCreateError(null);

    try {
      const sourceKey = createType === 'bundle' ? createEntityType : undefined;
      const viewMode = createType === 'bundle' ? 'list' : 'full';
      const result = await createDashboard({
        title: createTitle,
        viewMode,
        sourceKey,
      });

      if (sourceKey) {
        window.dispatchEvent(new CustomEvent('dashboard:open-editor', {
          detail: { uuid: result.uuid, sourceKey },
        }));
        onClose();
      } else {
        window.location.href = `/dashboard/${result.uuid}`;
      }
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Ошибка создания');
      setCreateLoading(false);
    }
  };

  const handleImport = async (dashboard: object, mode: 'create' | 'replace') => {
    if (mode === 'create') {
      const data = { ...dashboard } as Record<string, unknown>;
      if (data.uuid) {
        data.uuid = generateUUID();
      }
      await saveConfig(data as unknown as Dashboard);
    } else {
      await saveConfig(dashboard as unknown as Dashboard);
    }
    // Refresh dashboard list
    const response = await globalThis.fetch('/api/dashboards');
    if (response.ok) {
      const updatedList: DashboardSummary[] = await response.json();
      setDashboards(updatedList);
    }
    setShowImportModal(false);
    setToastMessage('Дашборд успешно импортирован');
    setTimeout(() => setToastMessage(null), 3000);
  };

  const groups = sortGroups(groupDashboards(dashboards)).map(g => ({
    ...g,
    items: sortItems(g.items),
  }));

  return (
    <div
      className="dashboard-list"
      role="dialog"
      aria-label="Список дашбордов"
      aria-modal="true"
      data-testid="dashboard-list"
      onClick={handleBackdropClick}
    >
      <div className="dashboard-list__header">
        <h5 className="mb-0">Дашборды</h5>
        <button
          type="button"
          className="btn-close"
          data-testid="dashboard-list-close"
          aria-label="Закрыть"
          onClick={onClose}
        />
      </div>
      <div className="dashboard-list__body" data-testid="dashboard-list-body">
        {loading && dashboards.length === 0 && (
          <div className="d-flex justify-content-center p-4">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Загрузка...</span>
            </div>
          </div>
        )}
        {error && (
          <div className="alert alert-danger m-2">Ошибка загрузки</div>
        )}
        {!loading && dashboards.length === 0 && !error && (
          <p className="text-muted p-3">Нет дашбордов</p>
        )}
        {dashboards.length > 0 && (
          <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3">
            {groups.map((group) => {
              const icon = getGroupIcon(parseBundleKey(group.items[0]?.sourceKey || '')?.entityType || '');
              const isCollapsed = collapsedGroups.has(group.label);
              return (
                <div key={group.label} className="col">
                  <div className="card" data-testid="dashboard-group">
                    <div
                      className="d-flex align-items-center gap-2 px-3 py-2 border-bottom"
                      role="button"
                      tabIndex={0}
                      aria-expanded={!isCollapsed}
                      onClick={() => toggleGroup(group.label)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggleGroup(group.label);
                        }
                      }}
                    >
                      <i className={icon} />
                      <span className="fw-semibold" style={{ fontSize: '0.85rem' }}>{group.label}</span>
                      <span className="badge bg-primary rounded-pill ms-auto" data-testid="dashboard-group-badge">
                        {group.items.length}
                      </span>
                      <i className={`las la-chevron-down chevron-toggle ${isCollapsed ? 'rotated' : ''}`} />
                    </div>
                    <div className={`list-group list-group-flush ${isCollapsed ? 'd-none' : ''}`}>
                      {group.items.map((dashboard) => {
                        const rawTitle = dashboard.title || dashboard.sourceKey;
                        const formattedDate = dashboard.updatedAt
                          ? new Date(dashboard.updatedAt).toLocaleDateString('ru-RU')
                          : '';
                        const itemIcon = getItemIcon(dashboard.sourceKey);
                        const badgeClass = dashboard.viewMode === 'full'
                          ? 'badge bg-primary'
                          : dashboard.viewMode === 'compact'
                            ? 'badge text-bg-info'
                            : 'badge bg-secondary';
                        const typeLabel = getTypeLabel(dashboard.type);
                        const typeBadgeClass = getTypeBadgeClass(dashboard.type);
                        return (
                          <div
                            key={dashboard.uuid}
                            className="list-group-item list-group-item-action d-flex align-items-center gap-3"
                            data-testid="dashboard-card"
                          >
                            {dashboard.type === 'standalone' && (
                              <div className="form-check" style={{ paddingLeft: 0 }}>
                                <input
                                  type="radio"
                                  className="form-check-input"
                                  name="home-page-dashboard"
                                  checked={dashboard.isHomePage}
                                  data-testid={`set-home-page-radio-${dashboard.uuid}`}
                                  title="Сделать главной страницей"
                                  onChange={() => handleSetHomePage(dashboard)}
                                  style={{ cursor: 'pointer', marginTop: 0 }}
                                />
                              </div>
                            )}
                            <div
                              className="dashboard-icon"
                              style={{ backgroundColor: 'var(--bs-primary-bg-subtle, #e7e9fc)' }}
                            >
                              <i className={`las ${itemIcon}`} style={{ color: 'var(--bs-primary, #3a57e8)' }} />
                            </div>
                            <div className="flex-grow-1 min-w-0">
                              <div className="fw-semibold" style={{ fontSize: '0.85rem' }}>
                                {rawTitle}
                              </div>
                              {dashboard.type === 'standalone' ? (
                                <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                                  <span style={{ fontFamily: 'monospace', fontSize: '0.68rem' }}>{dashboard.uuid}</span>
                                  {' · '}
                                  <a
                                    href={`/dashboard/${dashboard.uuid}`}
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ fontSize: '0.72rem' }}
                                  >
                                    /dashboard/{dashboard.uuid}
                                  </a>
                                </div>
                              ) : rawTitle !== dashboard.sourceKey ? (
                                <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                                  {dashboard.sourceKey}
                                </div>
                              ) : null}
                            </div>
                            <div className="d-flex align-items-center gap-2 flex-shrink-0">
                              <span className={typeBadgeClass}>{typeLabel}</span>
                              <span className={badgeClass}>{dashboard.viewMode}</span>
                              <span className={`badge ${dashboard.displayType === 'list' ? 'bg-info' : 'bg-secondary'}`} style={{ fontSize: '0.65rem' }}>
                                {dashboard.displayType === 'list' ? 'list' : 'single'}
                              </span>
                              {dashboard.type !== 'standalone' && (
                                <i
                                  className="las la-cog"
                                  style={{ cursor: 'pointer', fontSize: '1.25rem' }}
                                  title="Настройки"
                                  data-testid="dashboard-settings-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.dispatchEvent(new CustomEvent('dashboard:open-editor', {
                                      detail: { uuid: dashboard.uuid, sourceKey: dashboard.sourceKey },
                                    }));
                                  }}
                                />
                              )}
                              <i
                                className="las la-trash-alt text-danger opacity-50"
                                style={{ cursor: 'pointer', fontSize: '1.25rem' }}
                                title="Удалить"
                                data-testid="delete-dashboard-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDashboardToDelete(dashboard);
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
                                onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.5')}
                              />
                              {formattedDate && (
                                <small className="text-muted d-none d-sm-inline" style={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                                  {formattedDate}
                                </small>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div className="dashboard-list__footer">
        <button
          type="button"
          className="btn btn-outline-primary"
          data-testid="dashboard-list-import"
          onClick={() => setShowImportModal(true)}
        >
          <i className="las la-file-import"></i> Импорт
        </button>
        <button
          type="button"
          className="btn btn-primary"
          data-testid="dashboard-list-create"
          onClick={handleOpenCreateModal}
        >
          Создать дашборд
        </button>
      </div>

      {showCreateModal && (
        <div
          className="modal show d-block"
          tabIndex={-1}
          role="dialog"
          aria-label="Создать дашборд"
          aria-modal="true"
          data-testid="dashboard-create-modal"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Создать дашборд</h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Закрыть"
                  onClick={handleCloseCreateModal}
                  disabled={createLoading}
                />
              </div>
              <form onSubmit={handleCreateSubmit}>
                <div className="modal-body">
                  {createError && (
                    <div className="alert alert-danger" role="alert">
                      {createError}
                    </div>
                  )}
                  <div className="mb-3">
                    <label htmlFor="dashboard-title" className="form-label">
                      Название
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="dashboard-title"
                      value={createTitle}
                      onChange={(e) => setCreateTitle(e.target.value)}
                      placeholder="New Dashboard"
                      disabled={createLoading}
                      autoFocus
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="dashboard-type" className="form-label">
                      Тип
                    </label>
                    <select
                      className="form-select"
                      id="dashboard-type"
                      value={createType}
                      onChange={(e) => {
                        setCreateType(e.target.value as 'standalone' | 'bundle');
                        setCreateEntityType('');
                      }}
                      disabled={createLoading}
                    >
                      <option value="standalone">Стандартный — отдельная страница</option>
                      <option value="bundle">Для списка — на страницах списка</option>
                    </select>
                  </div>
                  {createType === 'bundle' && (
                    <div className="mb-3">
                      <label htmlFor="dashboard-entity-type" className="form-label">
                        Сущность
                      </label>
                      <select
                        className="form-select"
                        id="dashboard-entity-type"
                        value={createEntityType}
                        onChange={(e) => setCreateEntityType(e.target.value)}
                        disabled={createLoading}
                      >
                        <option value="">— Выберите —</option>
                        {ENTITY_TYPES.map((et) => (
                          <option key={et.sourceKey} value={et.sourceKey}>{et.label}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCloseCreateModal}
                    disabled={createLoading}
                  >
                    Отмена
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={createLoading || (createType === 'bundle' && !createEntityType)}
                    data-testid="dashboard-create-submit"
                  >
                    {createLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                        Создание...
                      </>
                    ) : (
                      'Создать'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
          <div
            className="modal-backdrop show"
            onClick={handleCloseCreateModal}
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: -1 }}
          />
        </div>
      )}

      {showImportModal && (
        <ImportModal
          onClose={() => setShowImportModal(false)}
          onImport={handleImport}
          dashboards={dashboards}
        />
      )}

      {dashboardToDelete && (
        <ConfirmationDeleteModal
          dashboard={dashboardToDelete}
          onConfirm={async () => {
            setDeleteLoading(true);
            try {
              await deleteDashboard(dashboardToDelete.uuid);
              setDashboards(prev => prev.filter(d => d.uuid !== dashboardToDelete.uuid));
              setDashboardToDelete(null);
              setToastMessage('Дашборд удалён');
              setTimeout(() => setToastMessage(null), 3000);
            } catch {
            } finally {
              setDeleteLoading(false);
            }
          }}
          onCancel={() => setDashboardToDelete(null)}
          loading={deleteLoading}
        />
      )}

      {homePageCandidate && (
        <ConfirmHomePageModal
          dashboard={homePageCandidate}
          onConfirm={handleHomePageConfirmed}
          onCancel={() => setHomePageCandidate(null)}
        />
      )}

      {toastMessage && (
        <div
          className="alert alert-success position-fixed bottom-0 end-0 m-3"
          role="alert"
          style={{ zIndex: 9999 }}
          data-testid="delete-toast"
        >
          {toastMessage}
        </div>
      )}
    </div>
  );
}
